// stores/useOfflineSlotsStore.ts

import type { AppProps, BlockedSlots } from '@/types/appTypes';
import axios, { isAxiosError } from 'axios';
import { ref, type Ref } from 'vue';

export type OfflineslotsStore = ReturnType<typeof useOfflineSlotsStore>;

// export type BlockedSlots = Record<string, number[]>;

export const useOfflineSlotsStore = () => {
    // This in now an array of date string keys (YYYY-MM-DD)
    // with an array of offline slot objects as values. Example:
    // "2026-04-15": [1, 2, 4, 5, 8]
    const offlineSlots: Ref<BlockedSlots> = ref({});

    const maxSlots = ref(-1); // Default max slots, will be updated from props

    /**
     * Get the offline slots for a specific date.
     * @param dateStr - Date in YYYY-MM-DD format
     * @returns Array of offline slot numbers (ints) for the date, or empty array if none
     */
    function getOfflineSlotsForDate(dateStr: string): number[] {
        if (!_isValidDateString(dateStr)) {
            console.warn(`Invalid date string in OfflineSlotsStore.getOfflineSlotsForDate(): ${dateStr}`);
            return [];
        }
        return offlineSlots.value[dateStr] || [];
    }

    /**
     * Check if a specific slot is offline for a given date.
     * @param dateStr - Date in YYYY-MM-DD format
     * @param slotNr - Slot number to check
     * @returns boolean indicating if the slot is offline
     */
    function isSlotOffline(dateStr: string, slotNr: number): boolean {
        if (!_isValidDateString(dateStr)) {
            console.warn(`Invalid date string in OfflineSlotsStore.isSlotOffline(): ${dateStr}`);
        }

        // 1. Get offline slots for this date
        const slotsNrsForDate = offlineSlots.value[dateStr] || [];

        // 2. If no slots for this date → not offline
        if (slotsNrsForDate.length === 0) {
            return false;
        }

        // 3. See if we have a matching slot number in the offline slots for this date
        for (const aSlotNr of slotsNrsForDate) {
            if (!aSlotNr || typeof aSlotNr !== 'number') {
                continue;
            }
            if (aSlotNr === slotNr) {
                return true;
            }
        }

        return false; // no slot matched → not offline
    }

    /**----------------------------------------------------
     * Mark a specific slot for a date as ONLINE.
     * Mutates offlineSlots[dateStr] (array of ints) and persists to server.
     *
     * Example: before → "2026-04-15": [1, 2, 4, 5]
     *          after  → "2026-04-15": [1, 2, 4, 5, 8]
     *
     * @param dateStr - Date in YYYY-MM-DD format
     * @param slotNr - Slot number (1–20)
     * @returns boolean success
     ----------------------------------------------------*/
    async function setADateSlotOnline(dateStr: string, slotNr: number): Promise<boolean> {
        if (!_isValidDateString(dateStr)) {
            console.warn(`Invalid date string in OfflineSlotsStore.setADateSlotOnline(): ${dateStr}`);
            return false;
        }

        if (!Number.isInteger(slotNr) || slotNr <= 0 || slotNr > maxSlots.value) {
            console.warn(`Invalid slotNr in OfflineSlotsStore.setADateSlotOnline(): ${slotNr}`);
            return false;
        }

        // Ensure array exists
        if (!Array.isArray(offlineSlots.value[dateStr])) {
            offlineSlots.value[dateStr] = [];
        }

        const current: number[] = offlineSlots.value[dateStr];

        // Already online (not in offline list)? → nothing to do
        if (!current.includes(slotNr)) {
            return true;
        }

        // 1) OPTIMISTIC: remove slot from array
        const newOfflineSlotNrs = current.filter((n) => n !== slotNr);
        const originalOfflineSlots = [...current];
        offlineSlots.value[dateStr] = newOfflineSlotNrs;

        // 2) Save to server; on success keep change, on failure revert
        try {
            await saveDatesOfflineSlots(dateStr, newOfflineSlotNrs);
            return true;
        } catch (error: unknown) {
            offlineSlots.value[dateStr] = originalOfflineSlots;

            if (isAxiosError(error)) {
                console.error('setADateSlotOnline() failed:', error.response?.data?.error || error.message);
                // TODO: user notification (toast)
            }

            return false;
        }
    }

    /**----------------------------------------------------
     * Mark a specific slot for a date as OFFLINE.
     * Mutates offlineSlots[dateStr] (array of ints) and persists to server.
     *
     * Example: before → "2026-04-15": [1, 2, 4, 5]
     *          after  → "2026-04-15": [1, 2, 4, 5, 8]
     *
     * @param dateStr - Date in YYYY-MM-DD format
     * @param slotNr - Slot number (1–20)
     * @returns boolean success
     ----------------------------------------------------*/
    async function setADateSlotOffline(dateStr: string, slotNr: number): Promise<boolean> {
        if (!_isValidDateString(dateStr)) {
            console.warn(`Invalid date string in OfflineSlotsStore.setADateSlotOffline(): ${dateStr}`);
            return false;
        }

        if (!Number.isInteger(slotNr) || slotNr <= 0 || slotNr > maxSlots.value) {
            console.warn(`Invalid slotNr in OfflineSlotsStore.setADateSlotOffline(): ${slotNr}`);
            return false;
        }

        // Ensure array exists
        if (!Array.isArray(offlineSlots.value[dateStr])) {
            offlineSlots.value[dateStr] = [];
        }

        const current: number[] = offlineSlots.value[dateStr];
        if (current.includes(slotNr)) {
            // Already offline; nothing to do
            return true;
        }

        // 1) OPTIMISTIC: add slot to array
        const newOfflineSlotNrs = [...current, slotNr];
        const originalOfflineSlots = [...current];
        offlineSlots.value[dateStr] = newOfflineSlotNrs;

        // 2) Save to server; on success keep change, on failure revert
        try {
            await saveDatesOfflineSlots(dateStr, newOfflineSlotNrs);
            return true;
        } catch (error: unknown) {
            offlineSlots.value[dateStr] = originalOfflineSlots;

            if (isAxiosError(error)) {
                console.error('setADateSlotOffline() failed:', error.response?.data?.error || error.message);
                // TODO: User notification (toast)
            }

            return false;
        }
    }

    /**----------------------------------------------------
     * Set all of a date's Slots to OFFLINE
     * @param dateStr - Date in YYYY-MM-DD format
     * @returns boolean success
     ----------------------------------------------------*/
    async function setAllSlotsOnlineForDate(dateStr: string): Promise<boolean> {
        if (!_isValidDateString(dateStr)) {
            console.warn(`Invalid date string in OfflineSlotsStore.setAllSlotsOnlineForDate(): ${dateStr}`);
            return false;
        }

        const slots: number[] = [];

        // Saves to server and returns success boolean.
        // UI is optimistically updated in this function, then reverted on failure.
        return await saveDatesOfflineSlots(dateStr, slots);
    }

    /**----------------------------------------------------
     * Set all of a date's Slots to OFFLINE
     * @param dateStr - Date in YYYY-MM-DD format
     * @returns boolean success
     ----------------------------------------------------*/
    async function setAllSlotsOfflineForDate(dateStr: string): Promise<boolean> {
        if (!_isValidDateString(dateStr)) {
            console.warn(`Invalid date string in OfflineSlotsStore.setAllSlotsOfflineForDate(): ${dateStr}`);
            return false;
        }

        const slots = [];
        for (let i = 1; i <= maxSlots.value; i++) {
            slots.push(i);
        }

        // Saves to server and returns success boolean.
        // UI is optimistically updated in this function, then reverted on failure.
        return await saveDatesOfflineSlots(dateStr, slots);
    }

    /**=========================================================================================================
     * Save offline slots for a date (create or update).
     * Optimistically updates the UI, then tries to save to server. Always reverts to server state on failure.
     * Sends YYYY-MM-DD string and array of offline slot integers (ints) to Laravel controller.
     *
     * Example of what's sent to server:
     * {date: "2026-04-15", slots: [1, 2, 4, 5, 8]}
     *
     * @param dateStr - Date in YYYY-MM-DD format
     * @param offlineSlots - Array of integers representing offline slots
     * @returns boolean success
     ==========================================================================================================*/
    async function saveDatesOfflineSlots(dateStr: string, newOfflineSlotNrs: number[]): Promise<boolean> {
        // 1) ===== Validate inputs first =====
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(dateStr)) {
            console.warn('dateStr must be in YYYY-MM-DD format in saveDatesOfflineSlots');
            return false;
        }

        if (!Array.isArray(newOfflineSlotNrs) || newOfflineSlotNrs.some((slot) => typeof slot !== 'number' || !Number.isInteger(slot))) {
            console.warn('newOfflineSlotNrs must be an array of integers in saveOfflineSlots');
            return false;
        }

        // Ensure reactive array exists
        if (!Array.isArray(offlineSlots.value[dateStr])) {
            offlineSlots.value[dateStr] = [];
        }

        // 2) ===== OPTIMISTIC UPDATE =====
        //    (store numbers, not objects)
        const originalOfflineSlots: number[] = [...(offlineSlots.value[dateStr] || [])];
        offlineSlots.value[dateStr] = [...newOfflineSlotNrs];

        // ===== SERVER CALL =====
        try {
            type OfflineSlotsResponse = { success: boolean };

            // Server call (adjust endpoint to match your Laravel route)
            await axios.post<OfflineSlotsResponse>('/offlineslots', {
                date: dateStr,
                slots: newOfflineSlotNrs,
            });

            // const serversOfflineSlots = Array.isArray(response.data) ? response.data : [];

            // // 1) Turn server payload into BlockedSlots
            // const normalized: BlockedSlots = {};
            // for (const aDateOjb of serversOfflineSlots) {
            //     // dto.slotnrs is number[]; assign whole array under date
            //     normalized[aDateOjb.date] = [...aDateOjb.slotnr];
            // }

            // // 2) Merge into store
            // offlineSlots.value = {
            //     ...offlineSlots.value,
            //     ...normalized,
            // };

            return true;
        } catch (error: unknown) {
            // ===== ALWAYS REVERT optimistic changes =====
            offlineSlots.value[dateStr] = originalOfflineSlots;

            if (isAxiosError(error)) {
                console.error('saveDatesOfflineSlots failed:', error.response?.data?.error || error.message);
                // TODO: User notification (toast)
            }
            return false;
        }
    }

    /**
     * This gets called from initStores syncStoresFromProps()
     * In theory it should always get the latest data required for this store
     * whenever the usePage().props changes on navigation.
     * @param props
     */
    const _updateFromProps = (props: AppProps) => {
        // CHANGE: Shallow clone prevents external mutations. Retains reactivity
        // through the entire bookingsRange object.
        maxSlots.value = props.maxSlots;
        offlineSlots.value = props.blockedSlots;

        // console.log(props);
        //console.log('OfflineSlotsStore updated from props. Current state:', offlineSlots.value);

    };

    return {
        isSlotOffline,
        getOfflineSlotsForDate,
        // ---
        setAllSlotsOfflineForDate,
        setAllSlotsOnlineForDate,
        // ---
        setADateSlotOffline,
        setADateSlotOnline,
        // ---
        maxSlots,
        // Updater from init stores
        updateFromProps: _updateFromProps,
    };

    // Make sure to validate date string format
    function _isValidDateString(param: unknown): param is string {
        if (typeof param !== 'string') return false;

        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(param)) return false;

        const date = new Date(param + 'T00:00:00Z');
        return date.toISOString().startsWith(param);
    }
};
